import React from 'react'
import { CheckCircle, AlertTriangle, MoreHorizontal } from 'lucide-react'
import ThermometerWithGate from '@/modules/termometro/components/ThermometerWithGate'
import { AsyncContentProgress, DropdownMenu, Badge } from '@/shared/ui'
import { useOportunidadeModal } from '@/modules/oportunidades/context/OportunidadeModalContext'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import ObjectiveIcon from '@/modules/oportunidades/components/ObjectiveIcon'
import { SignalCard } from '@/modules/signals/components/SignalCard'
import { useLastPrice } from '@/modules/signals/hooks/useLastPrice'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { GlowingEffect } from '@/components/ui/glowing-effect'
import { useInOutStatus } from '@/modules/in-out/hooks/useInOutStatus'
import { useInOutFeedback } from '@/modules/in-out/context/InOutFeedbackContext'
import { SignalCopyPanel } from './SignalCopyPanel'
import { shouldShowMultiTfCriticalWarning } from '../utils/multiTfWarning'
import { OpportunityCommunityEntry } from '@/modules/comunidade/components/OpportunityCommunityEntry'
import type { CommunitySummary } from '@/modules/comunidade/services/communityService'
import { OpportunityCommunityPnlRow } from './OpportunityCommunityPnlRow'

type MultiTfDirectionConflict = {
  hasConflict: boolean
  higherTf: string | null
}

type Props = {
  item: OpportunityItem
  thermoAllowed: boolean
  noteForMaster?: string
  signalsEnabled: boolean
  inOutEnabled?: boolean
  highlight?: boolean
  multiTfState?: 'ok' | 'warning' | 'error'
  multiTfDirection?: MultiTfDirectionConflict
  onViewed?: (id: string) => void
  onOpenActions?: () => void
  copyMode?: boolean
  onCopyRequested?: (id: string) => void
  onCopyClose?: (id: string) => void
  communityEnabled?: boolean
  communitySummary?: CommunitySummary | null
  resultsEnabled?: boolean
  onOpenResults?: (item: OpportunityItem) => void
}

export function OpportunitySignalsRow({
  item,
  thermoAllowed,
  noteForMaster,
  signalsEnabled,
  inOutEnabled = false,
  highlight,
  multiTfState,
  multiTfDirection,
  onViewed,
  onOpenActions,
  copyMode,
  onCopyRequested,
  onCopyClose,
  communityEnabled,
  communitySummary,
  resultsEnabled,
  onOpenResults,
}: Props) {
  const { openModal, openDebug, openReport } = useOportunidadeModal()
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const hasResults = !!resultsEnabled && typeof onOpenResults === 'function'

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    if (isVisible) return

    const el = containerRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      {
        rootMargin: '200px 0px 0px 0px',
      },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [isVisible])

  const { signals, loading: signalsLoading } = useOpportunitySignals(
    item.id,
    signalsEnabled && isVisible,
  )
  const inOut = useInOutStatus(Number(item.id))
  const lastPrice = useLastPrice(item.symbol)
  const { formatUserDateTime } = useUserDateFormatter()
  const { showIn, showOut } = useInOutFeedback()

  const publishedAtText = React.useMemo(
    () => (item.publishedAt ? formatUserDateTime(item.publishedAt) : null),
    [item.publishedAt, formatUserDateTime],
  )

  const handleOpenDetails = React.useCallback(() => {
    try {
      onViewed?.(String(item.id))
    } catch {
      // noop
    }
    openModal({
      id: item.id,
      symbol: item.symbol,
      name: item.name,
      logo_url: item.logo_url,
      kind: 'opportunity',
    })
  }, [item.id, item.logo_url, item.name, item.symbol, onViewed, openModal])

  const handleOpenReport = React.useCallback(() => {
    openReport({
      id: item.id,
      symbol: item.symbol,
      name: item.name,
      logo_url: item.logo_url,
      kind: 'opportunity',
    })
  }, [item.id, item.logo_url, item.name, item.symbol, openReport])

  const handleOpenDebug = React.useCallback(() => {
    openDebug({
      id: item.id,
      symbol: item.symbol,
      name: item.name,
      logo_url: item.logo_url,
      kind: 'opportunity',
    })
  }, [item.id, item.logo_url, item.name, item.symbol, openDebug])

  const menuItems = React.useMemo(() => {
    const items: { label: string; onSelect: () => void; disabled?: boolean }[] = []

    if (isMaster) {
      items.push({
        label: 'Detalhes',
        onSelect: handleOpenDetails,
      })
    } else {
      items.push({
        label: 'Ver gráfico',
        onSelect: handleOpenDetails,
      })
    }

    if (hasResults && onOpenResults) {
      items.push({
        label: 'Resultado',
        onSelect: () => onOpenResults(item),
      })
    }

    if (isMaster || inOutEnabled) {
      items.push({
        label: inOut.isIn ? 'Estou fora' : 'Estou dentro',
        onSelect: () => {
          if (inOut.loading || inOut.toggling) return
          const targetIsIn = !inOut.isIn
          void (async () => {
            await inOut.toggle()
            if (targetIsIn) {
              showIn(item.symbol, item.name, {
                opportunityId: item.id,
                tfMandante: signals?.tfMandante ?? null,
              })
            } else {
              showOut(item.symbol, item.name, {
                opportunityId: item.id,
                tfMandante: signals?.tfMandante ?? null,
              })
            }
          })()
        },
        disabled: inOut.loading || inOut.toggling,
      })
    }

    if (onCopyRequested) {
      items.push({
        label: 'Copiar dados',
        onSelect: () => onCopyRequested(String(item.id)),
      })
    }

    items.push({
      label: 'Relatar Bug',
      onSelect: handleOpenReport,
    })

    if (isMaster) {
      items.push({
        label: 'Debug',
        onSelect: handleOpenDebug,
      })
    }

    return items
  }, [
    handleOpenDebug,
    handleOpenDetails,
    handleOpenReport,
    inOut.isIn,
    inOut.loading,
    inOut.toggling,
    inOutEnabled,
    isMaster,
    item.id,
    item.name,
    item.symbol,
    onCopyRequested,
    signals,
    showIn,
    showOut,
    hasResults,
    onOpenResults,
  ])

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (copyMode) return

    const target = e.target as HTMLElement | null
    if (target && typeof target.closest === 'function') {
      const inCommunityZone = target.closest('[data-community-zone="true"]')
      if (inCommunityZone) return
    }

    onOpenActions?.()
  }

  const criticalWarning = shouldShowMultiTfCriticalWarning({
    multiTfState,
    signals: signals ?? null,
    lastPrice: typeof lastPrice === 'number' ? lastPrice : null,
  })

  const directionConflictWarning = !!multiTfDirection?.hasConflict

  const isIconPending =
    signalsEnabled &&
    ((signalsLoading && !signals) || (!multiTfState && !multiTfDirection))

  const effectiveIcon: React.ReactNode = !signalsEnabled
    ? <CheckCircle className="h-4 w-4 text-emerald-400" />
    : isIconPending
    ? null
    : criticalWarning || directionConflictWarning
    ? <AlertTriangle className="h-4 w-4 text-amber-400" />
    : <CheckCircle className="h-4 w-4 text-emerald-400" />

  return (
    <div
      ref={containerRef}
      id={`opp-card-${item.id}`}
      className={[
        'relative flex flex-col rounded-xl border-[4px] bg-[#1a1a1a] px-4 pt-4 cursor-pointer lg:cursor-default',
        communityEnabled ? 'pb-0' : 'pb-4',
        highlight ? 'ring-2 ring-lime-400 border-lime-400' : 'border-[#262626]',
      ].join(' ')}
      role="button"
      onClick={handleCardClick}
    >
      <GlowingEffect
        spread={40}
        glow
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
        borderWidth={3}
      />

      <div className="relative">
        {copyMode && signals && (
          <div className="absolute inset-0 z-20 rounded-xl bg-[#101010] p-3">
            <SignalCopyPanel
              signals={signals}
              onClose={() => onCopyClose?.(String(item.id))}
            />
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-1 items-center gap-3">
            {item.logo_url ? (
              <img
                src={item.logo_url}
                alt={item.symbol}
                className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="h-10 w-10 flex-shrink-0 rounded-full bg-muted" />
            )}

            <div className="flex flex-1 items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold sm:text-base">
                  {item.name || item.symbol}
                </div>
                <div className="truncate text-xs text-[#8a8a8a] sm:text-sm">
                  {item.symbol}
                </div>
              </div>

              <div className="min-w-0 text-right">
                <div className="flex flex-nowrap items-center justify-end gap-2 text-[11px] sm:text-xs md:text-sm font-semibold">
                  <span className="whitespace-nowrap text-foreground">
                    {[item.fractalLower, item.fractalUpper].filter(Boolean).join(' | ') || '-'}
                  </span>
                  <span
                    className={[
                      'whitespace-nowrap font-semibold text-[10px] sm:text-xs md:text-sm',
                      item.objective === 'Long'
                        ? 'text-emerald-400'
                        : item.objective === 'Short'
                        ? 'text-red-400'
                        : 'text-foreground/80',
                    ].join(' ')}
                  >
                    {item.objective === 'Long' ? 'bullish' : item.objective === 'Short' ? 'bearish' : '-'}
                  </span>
                  <ObjectiveIcon objective={item.objective} />
                </div>
                <div className="mt-0.5 text-xs text-[#8a8a8a] sm:text-sm">
                  {item.recommendation || '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-stretch gap-2">
            <div className="w-px bg-[#161616]" />
            <DropdownMenu
              trigger={(
                <button
                  type="button"
                  className="inline-flex items-center rounded p-1 hover:bg-muted/30"
                >
                  <MoreHorizontal className="h-5 w-5 text-[#434343]" />
                </button>
              )}
              items={menuItems}
              align="end"
            />
          </div>
        </div>

        <div className="mt-3 h-px bg-[#161616]" />

        <div className="mt-3 flex flex-col gap-2">
          {signalsEnabled && (
            <AsyncContentProgress
              loading={signalsLoading && !signals}
              label="Aguarde, calculando dados..."
            >
              {signals && (
                <>
                  <SignalCard
                    signals={signals}
                    lastPrice={lastPrice}
                    dateText={publishedAtText}
                    footerRight={
                      inOut.isIn ? (
                        <Badge
                          variant="soft"
                          color="success"
                          className="px-2 py-[2px] text-[9px]"
                        >
                          Estou dentro
                        </Badge>
                      ) : null
                    }
                  />
                  <div className="mt-1 grid w-full grid-cols-[0.95fr,0.05fr] items-center gap-2">
                    <div className="relative w-full">
                      <ThermometerWithGate
                        enabled={thermoAllowed}
                        value={item.thermometerPercent ?? item.confluencePercent}
                        note={noteForMaster}
                        className="h-2.5 w-full"
                      />
                      {thermoAllowed && (
                        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground/80">
                          {(item.thermometerPercent ?? item.confluencePercent)}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-end">
                      {effectiveIcon}
                    </div>
                  </div>
                </>
              )}
            </AsyncContentProgress>
          )}

          {communityEnabled && (
            <div data-community-zone="true">
              <OpportunityCommunityEntry
                opportunityId={Number(item.id)}
                symbol={item.symbol}
                name={item.name}
                logoUrl={item.logo_url}
                enabled={!!communityEnabled}
                summary={communitySummary ?? null}
                renderFooter={(summaryRow) => (
                  <OpportunityCommunityPnlRow
                    communityEnabled={!!communityEnabled}
                    summaryContent={summaryRow}
                    signals={signals ?? null}
                  />
                )}
              />
            </div>
          )}
        </div>
    </div>
    </div>
  )
}
